import { useState, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { ReportStatus } from '../../../shared/interfaces/ReportStatus';
import { OfficerFilter, ActiveChip } from '../types';
import { useDebounce } from '../../../shared/hooks/useDebounce';

export const useReportFilters = (setPage: (page: number) => void) => {
    const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
    const [dateTo, setDateTo] = useState<Dayjs | null>(null);
    const [maxDateFrom, setMaxDateFrom] = useState<Dayjs | undefined>(dayjs());
    const [selectedStatuses, setSelectedStatuses] = useState<ReportStatus[]>([]);
    
    const [reportedDateFrom, setReportedDateFrom] = useState<Dayjs | null>(null);
    const [reportedDateTo, setReportedDateTo] = useState<Dayjs | null>(null);
    const [detailsFilter, setDetailsFilter] = useState<string>("");
    const [reportedByFilter, setReportedByFilter] = useState<OfficerFilter[]>([]);
    const [modifiedByFilter, setModifiedByFilter] = useState<OfficerFilter[]>([]);

    const debouncedDetailsFilter = useDebounce(detailsFilter);

    const applyQuickRange = (
        type: "modified" | "reported",
        range: "today" | "last7" | "last30" | "clear"
      ) => {
        const today = dayjs();
        const start7 = today.subtract(7, "day");
        const start30 = today.subtract(30, "day");
        if (type === "modified") {
          if (range === "clear") {
            setDateFrom(null);
            setDateTo(null);
          } else if (range === "today") {
            setDateFrom(today.startOf("day"));
            setDateTo(today.endOf("day"));
          } else if (range === "last7") {
            setDateFrom(start7.startOf("day"));
            setDateTo(today.endOf("day"));
          } else if (range === "last30") {
            setDateFrom(start30.startOf("day"));
            setDateTo(today.endOf("day"));
          }
        } else {
          if (range === "clear") {
            setReportedDateFrom(null);
            setReportedDateTo(null);
          } else if (range === "today") {
            setReportedDateFrom(today.startOf("day"));
            setReportedDateTo(today.endOf("day"));
          } else if (range === "last7") {
            setReportedDateFrom(start7.startOf("day"));
            setReportedDateTo(today.endOf("day"));
          } else if (range === "last30") {
            setReportedDateFrom(start30.startOf("day"));
            setReportedDateTo(today.endOf("day"));
          }
        }
        setPage(0);
      };

    const handleDateToChange = (newValue: Dayjs | null) => {
        setDateTo(newValue);
        setPage(0);
        // If dateFrom is after the new dateTo, reset dateFrom
        if (newValue && dateFrom && dateFrom.isAfter(newValue)) {
          setDateFrom(null);
        }
        // Update maxDateFrom to be the new dateTo or today if dateTo is cleared
        setMaxDateFrom(newValue || dayjs());
    };

    const handleDateFromChange = (newValue: Dayjs | null) => {
        // Only allow setting dateFrom if it's before or equal to dateTo
        if (!dateTo || (newValue && newValue.isSameOrBefore(dateTo))) {
          setDateFrom(newValue);
          setPage(0);
        }
    };

    const handleReportedDateToChange = (newValue: Dayjs | null) => {
        setReportedDateTo(newValue);
        setPage(0);
        if (newValue && reportedDateFrom && reportedDateFrom.isAfter(newValue)) {
          setReportedDateFrom(null);
        }
    };
    
    const handleReportedDateFromChange = (newValue: Dayjs | null) => {
        if (
          !reportedDateTo ||
          (newValue && newValue.isSameOrBefore(reportedDateTo))
        ) {
          setReportedDateFrom(newValue);
          setPage(0);
        }
    };

    const handleDetailsFilterChange = (
        event: React.ChangeEvent<HTMLInputElement>
      ) => {
        setDetailsFilter(event.target.value);
        setPage(0);
    };

    const handleResetFilters = () => {
        setDateFrom(null);
        setDateTo(null);
        setReportedDateFrom(null);
        setReportedDateTo(null);
        setDetailsFilter("");
        setReportedByFilter([]);
        setModifiedByFilter([]);
        setMaxDateFrom(dayjs());
        setSelectedStatuses([]);
        setPage(0);
    };

    const statusLabel = (s: ReportStatus) => {
        if (s === ReportStatus.RMS_COMMITTED) return "SUBMITTED";
        if (s === ReportStatus.AWAITING_INPUT) return "AWAITING INPUT";
        return s;
    };
    
    const activeChips = useMemo(() => {
        const chips: ActiveChip[] = [];
        if (dateFrom || dateTo) {
            chips.push({
              key: "modifiedRange",
              label: `Modified: ${
                dateFrom ? dayjs(dateFrom).format("MM/DD/YY") : "..."
              } - ${dateTo ? dayjs(dateTo).format("MM/DD/YY") : "..."}`,
              onDelete: () => {
                setDateFrom(null);
                setDateTo(null);
                setPage(0);
              },
            });
          }
        
          if (reportedDateFrom || reportedDateTo) {
            chips.push({
              key: "reportedRange",
              label: `Reported: ${
                reportedDateFrom ? dayjs(reportedDateFrom).format("MM/DD/YY") : "..."
              } - ${reportedDateTo ? dayjs(reportedDateTo).format("MM/DD/YY") : "..."}`,
              onDelete: () => {
                setReportedDateFrom(null);
                setReportedDateTo(null);
                setPage(0);
              },
            });
          }
        
          if (detailsFilter.trim()) {
            chips.push({
              key: "details",
              label: `Details: ${detailsFilter}`,
              onDelete: () => {
                setDetailsFilter("");
                setPage(0);
              },
            });
          }
        
          if (reportedByFilter.length > 0) {
            chips.push({
              key: "reportedBy",
              label: `Reported By: ${reportedByFilter.map((r) => r.name).join(", ")}`,
              onDelete: () => {
                setReportedByFilter([]);
                setPage(0);
              },
            });
          }
        
          if (modifiedByFilter.length > 0) {
            chips.push({
              key: "modifiedBy",
              label: `Modified By: ${modifiedByFilter.map((m) => m.name).join(", ")}`,
              onDelete: () => {
                setModifiedByFilter([]);
                setPage(0);
              },
            });
          }
        
          if (selectedStatuses.length) {
            chips.push({
              key: "statuses",
              label: `Status: ${selectedStatuses.map(statusLabel).join(", ")}`,
              onDelete: () => {
                setSelectedStatuses([]);
                setPage(0);
              },
            });
          }
          return chips;
    }, [dateFrom, dateTo, reportedDateFrom, reportedDateTo, detailsFilter, reportedByFilter, modifiedByFilter, selectedStatuses, setPage]);


    const hasActiveFilters = 
      dateFrom !== null ||
      dateTo !== null ||
      reportedDateFrom !== null ||
      reportedDateTo !== null ||
      detailsFilter.trim() !== "" ||
      reportedByFilter.length > 0 ||
      modifiedByFilter.length > 0 ||
      selectedStatuses.length > 0;
    
    const activeFiltersCount = 
        (dateFrom !== null || dateTo !== null ? 1 : 0) +
        (reportedDateFrom !== null || reportedDateTo !== null ? 1 : 0) +
        (detailsFilter.trim() !== "" ? 1 : 0) +
        (reportedByFilter.length > 0 ? 1 : 0) +
        (modifiedByFilter.length > 0 ? 1 : 0) +
        (selectedStatuses.length > 0 ? 1 : 0);

    return {
        dateFrom, setDateFrom,
        dateTo, setDateTo,
        maxDateFrom, setMaxDateFrom,
        selectedStatuses, setSelectedStatuses,
        reportedDateFrom, setReportedDateFrom,
        reportedDateTo, setReportedDateTo,
        detailsFilter, setDetailsFilter,
        reportedByFilter, setReportedByFilter,
        modifiedByFilter, setModifiedByFilter,
        debouncedDetailsFilter,
        
        applyQuickRange,
        handleDateToChange,
        handleDateFromChange,
        handleReportedDateToChange,
        handleReportedDateFromChange,
        handleDetailsFilterChange,
        handleResetFilters,
        
        activeChips,
        hasActiveFilters,
        activeFiltersCount
    };
}
